import { useQuery } from "react-query";

import { useApiService } from "../useApiService";
import {
  DataTranslationDto,
  DataTranslationValueDto,
  LanguageApi,
} from "../../api";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useEventsContext } from "../../contexts";
import { useLocalStorageState } from "ahooks";

export function useDisplayLanguages() {
  const languageApi = useApiService(LanguageApi);

  const { dispatchEvent } = useEventsContext();
  const { i18n } = useTranslation();

  const [languageFromStorage, setLanguageFromStorage] = useLocalStorageState(
    "myviapro-platform-language",
    {
      defaultValue: "fr",
    }
  );

  const { data, isFetching, refetch } = useQuery(
    "ref-languages",
    async () => {
      return languageApi.getLanguages();
    },
    { cacheTime: Infinity, initialData: [] }
  );

  const buildDefaultDataTranslations = React.useCallback(() => {
    const translations = data.map((l) =>
      DataTranslationValueDto.fromJS({
        languageId: l.id,
        languageCode: l.code,
        text: "",
      })
    );
    return DataTranslationDto.fromJS({
      dataTranslationValues: translations,
    });
  }, [data]);

  const currentLanguage = React.useMemo(() => {
    return data.find(
      (d) => d.code.toLowerCase() === i18n.language.toLowerCase()
    );
  }, [i18n.language, data]);

  const changeLanguage = React.useCallback(
    async (code: string) => {
      setLanguageFromStorage(code);
    },
    [setLanguageFromStorage]
  );

  useEffect(() => {
    i18n.changeLanguage(languageFromStorage.toLowerCase()).then(() => {
      dispatchEvent("LanguageChanged");
    });
  }, [dispatchEvent, i18n, languageFromStorage]);

  return {
    languages: data,
    loadingLanguages: isFetching,
    refetchLanguages: refetch,
    buildDefaultDataTranslations,
    currentLanguage,
    changeLanguage,
  };
}
